import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, HostBinding, inject, Injector, output, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { IDType } from '@core/models/types';
import { DEBOUNCE_TIME } from '@core/performance';
import { COMPANY_NAME_ID, RAPID_VIEWS_NAME_ID, ReferentialEntity, ReferentialsController, SAP_ID } from '@core/referentials';
import { AppConfigService } from '@core/services';
import { FeatureTypes, UserController } from '@core/user';
import { BaseMixinComponent } from '@mixins/components/base-component';
import { TranslatableComponentMixin } from '@mixins/components/translatable';
import { TranslateModule } from '@ngx-translate/core';
import { stopAndPreventEvent } from '@utils/dom/event';
import { MirIcon } from '@widgets/mir-icon';
import { MirOption, MirSelectComponent, MirSelectEvent } from '@widgets/select';
import { SlideCheckbox, SlideCheckboxEvent } from '@widgets/slide-checkbox';
import { SpinnerIcon } from '@widgets/spinner-icon';
import { WIZARD_PANEL_TOOLTIPS_KEY } from '@wizard/constants';
import { WizardPanelController } from '@wizard/wizard-panel/services';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

const SAVE_ICON = {
  'Save': 'save',
  'Duplicate': 'save_as'
} as const;

const SAVE_LABEL = {
  'Save': 'Save',
  'Duplicate': 'Duplicate'
} as const;

const LOCK_LABEL = {
  'Soft': 'Soft',
  'Hard': 'Hard'
} as const;

const OPTIONS_LABEL = {
  'Collapse': 'ClickToCollapse',
  'Expand': 'ClickToExpand'
} as const;

// 🧰 keep
const BASE_TRAD_KEY = `Wizard.Panel`;
const TOOLTIPS_TRAD_KEY = `${BASE_TRAD_KEY}.UI.Tooltips`;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mir-wizard-panel-controls',
  templateUrl: './wizard-panel-controls.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatMenuModule,
    MirIcon,
    SpinnerIcon,
    MirSelectComponent,
    SlideCheckbox,
  ]
})
export class WizardPanelControlsComponent extends TranslatableComponentMixin(BaseMixinComponent) {

  @HostBinding('class')
  get hostCssClass(): string {
    return `mir-wizard-panel-controls
            z-10 w-full h-fit
            grid grid-cols-1 justify-stretch
            bg-white
            shadow-2
            [&.locked]:bg-zinc-50
            [&.editable]:shadow-1
            transition-all`;
  }

  @HostBinding('class.no-basket')
  get noBasketCssClass(): boolean { return !this.hasBasket(); }

  @HostBinding('class.editable')
  get editableCssClass(): boolean { return this.isEditable(); }

  @HostBinding('class.locked')
  get lockedCssClass(): boolean { return this.isLocked(); }

  @HostBinding('class.options-opened')
  get optionsOpenedCssClass(): boolean { return this.optionsPanelOpened(); }

  /** @Output */
  readonly optionsPanelStateChange = output<boolean>();

  /** @Inject */
  readonly #appConfigService = inject(AppConfigService);
  readonly #userController = inject(UserController);
  readonly #referentialsControllers = inject(ReferentialsController);
  readonly #wizardPanelController = inject(WizardPanelController);

  protected readonly hasBasket = signal(false);
  protected readonly isEditing = signal(false);
  protected readonly canUseUndo = signal(false);
  protected readonly basketName = signal('');
  protected readonly hasBWMigration = signal(false);
  protected readonly isBWMigrationChecked = signal(false);

  protected readonly saveBtnTooltip = computed(() => {
    const isUserBasket = this.isUserBasket();
    return this.translate(`${WIZARD_PANEL_TOOLTIPS_KEY}.${isUserBasket ? SAVE_LABEL.Save : SAVE_LABEL.Duplicate}`);
  });

  protected readonly lockTooltip = computed(() => {
    const isUserBasket = this.isUserBasket();
    return this.translate(`${WIZARD_PANEL_TOOLTIPS_KEY}.Locked.${isUserBasket ? LOCK_LABEL.Soft : LOCK_LABEL.Hard}`);
  });

  protected readonly saveIcon = computed(() => SAVE_ICON[this.isUserBasket() ? SAVE_LABEL.Save : SAVE_LABEL.Duplicate]);

  protected readonly isSaving: Signal<boolean>;
  protected readonly isUserBasket: Signal<boolean>;
  protected readonly isEditable: Signal<boolean>;
  protected readonly isLocked: Signal<boolean>;
  protected readonly isBasketEmpty: Signal<boolean>;
  protected readonly canSave: Signal<boolean>;
  /* 🪛 dev 🪛*/
  protected readonly historyCount: Signal<number>;

  protected readonly basketNameFormControl = new FormControl({ value: '', disabled: false });

  protected readonly optionsPanelOpened = signal(true);
  protected readonly referentialList = signal<MirOption[]>([] as MirOption[]);
  protected readonly versionList = signal<MirOption[]>([] as MirOption[]);

  protected readonly optionsPanelTooltip = computed(() => {
    const optionsPanelOpened = this.optionsPanelOpened();
    return this.translate(`${TOOLTIPS_TRAD_KEY}.${optionsPanelOpened ? OPTIONS_LABEL.Collapse : OPTIONS_LABEL.Expand}`);
  });

  constructor() {
    const injector = inject(Injector);

    super();

    this.hasBWMigration.set(this.#userController.hasFeature(FeatureTypes.ReferentialCompanyBWConnector));

    this.canUseUndo.set(this.#userController.isRapidViewsUser && !this.#appConfigService.environment.production);

    this.isBasketEmpty = toSignal(
      this.#wizardPanelController.isBasketEmpty$.pipe(distinctUntilChanged()),
      { initialValue: true, injector }
    );

    this.isUserBasket = toSignal(
      this.#wizardPanelController.isUserBasket$.pipe(distinctUntilChanged()),
      { initialValue: false, injector }
    );

    this.isEditable = toSignal(
      this.#wizardPanelController.isEditable$.pipe(distinctUntilChanged()),
      { initialValue: false, injector }
    );

    this.isLocked = toSignal(
      this.#wizardPanelController.isLocked$.pipe(distinctUntilChanged()),
      { initialValue: true, injector }
    );

    this.isSaving = toSignal(
      this.#wizardPanelController.isSaving$.pipe(distinctUntilChanged()),
      { initialValue: false, injector }
    );

    /* 🪛 dev 🪛*/
    this.historyCount = toSignal(
      this.#wizardPanelController.historyList$.pipe(map(list => list?.length)),
      { initialValue: 0, injector }
    );

    effect(() => {
      const basket = this.#wizardPanelController.basket();

      if (basket) {
        this.hasBasket.set(true);
        this.basketName.set(basket.name);
        this.basketNameFormControl.patchValue(basket.name, { emitEvent: true });
        this.referentialList.set(this.referentialEntityListToOptionList(this.#referentialsControllers.referentialList));
        this.versionList.set(this.versionEntityListToOptionList(basket.referentialId));
        this.isBWMigrationChecked.set(Boolean(basket.bwMigration));
      } else {
        this.hasBasket.set(false);
        this.basketName.set('');
        this.referentialList.set([] as MirOption[]);
        this.versionList.set([] as MirOption[]);
      }
    });

    this.basketNameFormControl.valueChanges.pipe(
      takeUntilDestroyed(),
      distinctUntilChanged(),
      debounceTime(DEBOUNCE_TIME)
    ).subscribe(this.onBasketNameChange.bind(this));
  }

  handleCreateNewCopy(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.createNewCopy();
  }

  handleSaveBasket(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.saveBasket();
  }

  handleNewBasket(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.newBasket();
  }

  handleClearBasket(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.clearBasket();
  }

  handleDeleteBasket(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.deleteBasket();
  }

  handleCancelAddition(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.cancelLastAddition();
  }

  handleToggleOptionsPanel(event: MouseEvent) {
    const opened = this.optionsPanelOpened();

    stopAndPreventEvent(event);

    this.optionsPanelOpened.set(!opened);
    this.optionsPanelStateChange.emit(!opened);
  }

  handleChangeReferential(selectEvent: MirSelectEvent) {
    stopAndPreventEvent(selectEvent.event);
    const referentialId = selectEvent.option.id;

    this.#wizardPanelController.updateBasketReferential(referentialId).then(() => {
      this.versionList.set(this.versionEntityListToOptionList(referentialId));
    });
  }

  handleChangeVersion(selectEvent: MirSelectEvent) {
    stopAndPreventEvent(selectEvent.event);
    const versionId = selectEvent.option.id;

    this.#wizardPanelController.updateBasketVersion(versionId);
  }

  handleToggleBWMigration(event: SlideCheckboxEvent) {
    this.#wizardPanelController.updateBWMigrationFlag(event.checked);
  }

  private async onBasketNameChange(value: string) {
    return this.#wizardPanelController.updateBasketName(value);
  }

  private referentialEntityListToOptionList(referentials: ReferentialEntity[]): MirOption[] {
    const optionList = referentials.map((referential, index) => {
      const name = (referential.name === COMPANY_NAME_ID)
        ? this.#userController.company
        : (referential.name === RAPID_VIEWS_NAME_ID)
          ? this.#appConfigService.get('RVSName')
          : referential.name;

      return referential.id !== SAP_ID
        ? {
          index,
          id: referential.id,
          label: name,
          name,
          selected: this.isReferentialSelected(referential.id),
          disabled: false
        } as MirOption
        : null;
    }).filter(option => !!option) as MirOption[];

    return optionList;
  }

  private versionEntityListToOptionList(referentialId: IDType): MirOption[] {
    const referential = this.#referentialsControllers.getReferentialById(referentialId);

    if (referential && referential.versions) {
      const optionList = referential.versions.map((version, index) => {
        return {
          index,
          id: version.id,
          name: version.name,
          label: version.name,
          selected: this.isVersionSelected(version.id),
          disabled: false
        } as MirOption;
      }) as MirOption[];

      return optionList;
    }

    return [] as MirOption[];
  }

  private isReferentialSelected(referentialId: IDType) {
    return this.#wizardPanelController.basketReferential === SAP_ID || this.#wizardPanelController.basketReferential === referentialId;
  }

  private isVersionSelected(versionId: IDType) {
    return this.#wizardPanelController.basketVersion === versionId;
  }
}
